import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { Modal } from '../../components/ui/Modal.js';
import { Spinner } from '../../components/ui/Spinner.js';
import {
  CARTRACK_KEYS,
  type CarDoor,
  type CarImmobiliser,
  type DoorCommandWord,
  type ImmobiliserAction,
  type ImmobiliserCommand,
  cartrackApi,
  refusal,
} from '../../services/cartrack.js';
import { mytClock, mytDayLabel } from '../../utils/myt.js';

/**
 * 419 (#1529, mock-up sections 1–3): the car's Cartrack immobiliser. The platform decides who may read it (404
 * otherwise: no card) and who may command it (`can_command`); this card only words what it is told. Cartrack applies
 * an immobilise only with the engine off, so the card never suggests it can stop a moving car.
 */
const WARRANTY =
  "Any activation of the remote immobilisation function may impact negatively on Cartrack's ability to recover such vehicle, in the event of a theft of that vehicle. This may result in the rejection of a claim under the limited recovery warranty.";
const CUSTOMER_TOLD: Record<ImmobiliserAction, string> = {
  immobilise: 'Your car has been immobilised by CARii. Please contact us.',
  release: 'Your car has been released.',
};
const STATE_WORD: Record<CarImmobiliser['state'], string> = {
  released: 'Released',
  immobilised: 'Immobilised',
  unknown: 'Unknown',
  not_available: 'Not available',
};
const ENGINE_WORD: Record<CarImmobiliser['engine'], string> = {
  off: 'Off · parked',
  on: 'On · being driven',
  unknown: 'Unknown',
};
const ACTION_WORD: Record<ImmobiliserAction, string> = {
  immobilise: 'Immobilise',
  release: 'Release',
};
const DONE_WORD: Record<ImmobiliserAction, string> = {
  immobilise: 'Immobilised',
  release: 'Released',
};
const STATUS_NOTE: Record<ImmobiliserCommand['status'], string> = {
  sent: 'sent, waiting for the car',
  confirmed: '',
  unconfirmed: 'not confirmed by the car',
  refused: 'refused by Cartrack',
};
const opposite = (a: ImmobiliserAction): ImmobiliserAction =>
  a === 'immobilise' ? 'release' : 'immobilise';
const when = (iso: string) => `${mytDayLabel(iso)}, ${mytClock(iso)}`;
/** A refusal's own sentence, with "Nothing was sent." once (the platform's door refusals already say it). */
const nothingSent = (message: string) =>
  /nothing was sent/i.test(message) ? message : `${message} Nothing was sent.`;

/** Which command the card offers: the opposite of the state, or of an unconfirmed ask (to call it back). */
function nextAction(v: CarImmobiliser): ImmobiliserAction {
  const lastAsked = v.commands.find((c) => c.status !== 'refused');
  if (v.unconfirmed && lastAsked) return opposite(lastAsked.action);
  if (v.state === 'immobilised') return 'release';
  if (v.state === 'released') return 'immobilise';
  const lastConfirmed = v.commands.find((c) => c.status === 'confirmed');
  return lastConfirmed ? opposite(lastConfirmed.action) : 'immobilise';
}

export function CarImmobiliserCard({
  carId,
  plateNumber,
}: { carId: string; plateNumber?: string }) {
  const read = useQuery({
    queryKey: CARTRACK_KEYS.immobiliser(carId),
    queryFn: () => cartrackApi.immobiliser(carId),
  });
  const [asking, setAsking] = useState<ImmobiliserAction | null>(null);
  if (refusal(read.error)?.status === 404) return null;
  // Read by shape, like `refusal`: many page suites answer every GET with one payload, and the car page must load.
  const v = Array.isArray(read.data?.commands) ? read.data : undefined;

  const label = !v
    ? null
    : !v.configured
      ? 'Not set up'
      : !v.available
        ? 'Not available'
        : v.waiting
          ? 'Sent — waiting for the car to confirm'
          : v.unconfirmed
            ? 'Not confirmed by the car yet'
            : STATE_WORD[v.state];
  const lastAsked = v?.commands.find((c) => c.status !== 'refused');
  const offered = v ? nextAction(v) : null;

  return (
    <Card>
      <div data-testid="immobiliser-card">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-ink">Immobiliser</h2>
          {label && (
            <span
              data-testid="immobiliser-state"
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                v?.state === 'immobilised' && !v.waiting && !v.unconfirmed
                  ? 'bg-semantic-warning-bg text-semantic-warning-text'
                  : 'bg-surface text-slate'
              }`}
            >
              {label}
            </span>
          )}
        </div>
        {read.isPending && <Spinner />}
        {read.isError && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            Could not load the immobiliser. The rest of this page works as usual.
          </p>
        )}
        {v && !v.configured && (
          <p className="text-sm text-steel">
            The Cartrack command login is not set up here, so commands cannot be sent.
          </p>
        )}
        {v?.configured && !v.available && (
          <p className="text-sm text-steel">
            Cartrack cannot immobilise this car (no immobiliser fitted, or not on contract).
          </p>
        )}
        {v?.configured && v.available && (
          <>
            <dl className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-1 text-sm">
              {v.waiting && (
                <>
                  <dt className="text-steel">Asked</dt>
                  <dd>
                    {ACTION_WORD[v.waiting.action]} · {when(v.waiting.requested_at)}
                    {lastAsked?.requested_by_name ? ` by ${lastAsked.requested_by_name}` : ''}
                  </dd>
                </>
              )}
              {!v.waiting && v.unconfirmed && lastAsked && (
                <>
                  <dt className="text-steel">Asked</dt>
                  <dd>
                    {ACTION_WORD[lastAsked.action]} · {when(lastAsked.requested_at)}
                    {lastAsked.requested_by_name ? ` by ${lastAsked.requested_by_name}` : ''}
                  </dd>
                  <dt className="text-steel">Cartrack says</dt>
                  <dd>{STATE_WORD[v.state]}</dd>
                </>
              )}
              <dt className="text-steel">Engine now</dt>
              <dd>{ENGINE_WORD[v.engine]}</dd>
            </dl>
            {v.waiting && (
              <>
                <p className="mt-3 text-sm text-steel">
                  {v.waiting.action === 'immobilise'
                    ? 'Takes hold when the engine is turned off; then the car cannot be started. '
                    : ''}
                  Cartrack usually confirms within an hour.
                </p>
                <p className="mt-2 text-xs text-steel">
                  No other command until this one is confirmed
                </p>
              </>
            )}
            {!v.waiting && v.unconfirmed && (
              <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                The car has not confirmed the command. It may be out of signal. Check again later,
                or {offered === 'release' ? 'release' : 'immobilise'}.
              </p>
            )}
            {!v.waiting && !v.unconfirmed && v.state === 'immobilised' && (
              <p className="mt-3 text-sm text-steel">
                The car cannot be started until it is released.
              </p>
            )}
            {!v.waiting && !v.unconfirmed && v.state === 'unknown' && (
              <p className="mt-3 text-sm text-steel">
                Cartrack could not read this car's immobiliser just now. Commands can still be sent.
              </p>
            )}
            {!v.waiting && !v.can_command && (
              <p className="mt-3 text-sm text-steel">
                Only staff allowed to immobilise cars can immobilise or release it.
              </p>
            )}
            {(v.can_command || (!v.waiting && v.unconfirmed)) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {!v.waiting && v.unconfirmed && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    loading={read.isFetching}
                    onClick={() => read.refetch()}
                  >
                    Check again
                  </Button>
                )}
                {/* 420 (FR-001): both actions whenever a command may be sent; the state may lag, read Unknown, or have
                    been changed outside CARii. */}
                {v.can_command && (
                  <>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => setAsking('immobilise')}
                    >
                      Immobilise…
                    </Button>
                    <Button
                      type="button"
                      variant={v.state === 'immobilised' ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={() => setAsking('release')}
                    >
                      Release…
                    </Button>
                  </>
                )}
              </div>
            )}
          </>
        )}
        {v && v.commands.length > 0 && (
          <div className="mt-4">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-steel">
              Last commands
            </h3>
            <ul data-testid="immobiliser-history" className="space-y-1 text-sm">
              {v.commands.map((c) => (
                <li key={c.id}>
                  <span className="font-medium text-ink">
                    {c.status === 'confirmed' ? DONE_WORD[c.action] : ACTION_WORD[c.action]}
                  </span>
                  <span className="text-steel">
                    {' '}
                    by {c.requested_by_name || 'someone'} · {when(c.requested_at)}
                    {c.confirmed_at ? ` · confirmed ${mytClock(c.confirmed_at)}` : ''}
                    {STATUS_NOTE[c.status] ? ` · ${STATUS_NOTE[c.status]}` : ''}
                    {c.reason ? ` · “${c.reason}”` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {v && isDoor(v.door) && v.door.configured && (
          <DoorSection carId={carId} plateNumber={plateNumber} door={v.door} />
        )}
      </div>
      {asking && v && (
        <CommandDialog
          carId={carId}
          plateNumber={plateNumber}
          action={asking}
          engine={v.engine}
          state={v.state}
          onClose={() => setAsking(null)}
        />
      )}
    </Card>
  );
}

function CommandDialog({
  carId,
  plateNumber,
  action,
  engine,
  state,
  onClose,
}: {
  carId: string;
  plateNumber?: string;
  action: ImmobiliserAction;
  engine: CarImmobiliser['engine'];
  state: CarImmobiliser['state'];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [agreed, setAgreed] = useState(false);
  const immobilise = action === 'immobilise';
  const send = useMutation({
    mutationFn: () =>
      cartrackApi.commandImmobiliser(
        carId,
        immobilise
          ? { action, reason: reason.trim(), warranty_agreed: true }
          : { action, reason: reason.trim() },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CARTRACK_KEYS.immobiliser(carId) });
      onClose();
    },
  });
  const ready = reason.trim() !== '' && (!immobilise || agreed);
  const failed =
    send.error instanceof Error ? send.error.message : send.error ? 'The command failed.' : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`${ACTION_WORD[action]} ${plateNumber ?? 'this car'}?`}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={immobilise ? 'danger' : 'primary'}
            disabled={!ready}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            {ACTION_WORD[action]}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        {/* 420 (FR-002): the action matches the last confirmed state; it is still allowed. */}
        {state === (immobilise ? 'immobilised' : 'released') && (
          <p
            data-testid="immobiliser-already"
            className="rounded-md bg-primary/10 px-3 py-2 text-ink"
          >
            Cartrack already reports this car {immobilise ? 'immobilised' : 'released'}. Send the{' '}
            {immobilise ? 'immobilise' : 'release'} anyway? (Use this when the state looks wrong or
            a command was sent outside CARii.)
          </p>
        )}
        {immobilise ? (
          <p
            data-testid="immobilise-engine-note"
            data-tone={engine === 'off' ? 'ok' : 'warn'}
            className={`rounded-md px-3 py-2 ${
              engine === 'off'
                ? 'bg-semantic-success-bg text-semantic-success-text'
                : 'border border-amber-300 bg-amber-50 text-amber-800'
            }`}
          >
            {engine === 'off'
              ? 'Engine off · parked. Takes hold at once. The car cannot be started until it is released.'
              : engine === 'on'
                ? 'Engine on · being driven. It will be immobilised when the engine is turned off, and cannot be started again after that. It never stops the car while driven.'
                : 'Engine state unknown. It takes hold once the engine is off, and cannot be started again after that. It never stops the car while driven.'}
          </p>
        ) : (
          <p className="text-steel">The car can be started again once Cartrack confirms.</p>
        )}
        <div>
          <label htmlFor="immobiliser-reason" className="mb-1 block font-medium text-ink">
            Reason
          </label>
          <textarea
            id="immobiliser-reason"
            rows={2}
            maxLength={300}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-md border border-hairline-strong bg-canvas px-3 py-2 text-ink"
          />
          <p className="mt-1 text-xs text-steel">
            Required. Kept on the car's record. The customer is not shown it.
          </p>
        </div>
        {immobilise && (
          <div className="rounded-md border border-hairline-strong px-3 py-2">
            <p className="mb-1 font-medium text-ink">Cartrack’s warning</p>
            <p className="text-steel">{WARRANTY}</p>
            <label className="mt-2 flex items-center gap-2 text-ink">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              I have read and agree to Cartrack's warning.
            </label>
          </div>
        )}
        <p className="text-steel">The customer is told in the app: “{CUSTOMER_TOLD[action]}”</p>
        {failed && (
          <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-red-700">
            {nothingSent(failed)}
          </p>
        )}
      </div>
    </Modal>
  );
}

/** 420: the `door` block, read by shape (an older backend sends none). */
function isDoor(d: unknown): d is CarDoor {
  return !!d && typeof d === 'object' && Array.isArray((d as CarDoor).commands);
}

const DOOR_WORD: Record<DoorCommandWord, string> = { lock: 'Lock', unlock: 'Unlock' };
const DOOR_DONE: Record<DoorCommandWord, string> = { lock: 'locked', unlock: 'unlocked' };
const DOOR_REFUSED: Record<string, string> = {
  busy: 'sent moments before',
  not_supported: 'no lock device, or the tracker could not be reached',
  not_found: 'no lock device, or the tracker could not be reached',
  forbidden: "CARii's Cartrack login is not allowed",
};

/**
 * 420 (mock-up sections 1–2): lock or unlock the doors through Cartrack. Cartrack never reports whether the doors moved,
 * so the card only says what was sent; the platform decides who may (`door.can_command`).
 */
function DoorSection({
  carId,
  plateNumber,
  door,
}: {
  carId: string;
  plateNumber?: string;
  door: CarDoor;
}) {
  const [asking, setAsking] = useState<DoorCommandWord | null>(null);
  const [sent, setSent] = useState<DoorCommandWord | null>(null);
  return (
    <div data-testid="door-section" className="mt-4 border-t border-hairline pt-4">
      <h3 className="mb-2 text-sm font-semibold text-ink">Doors</h3>
      <p className="text-sm text-steel">
        Cartrack locks or unlocks the doors through the car's key-fob device. It does not report
        whether the doors are locked.
      </p>
      {door.can_command ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => setAsking('lock')}>
            Lock doors…
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => setAsking('unlock')}>
            Unlock doors…
          </Button>
        </div>
      ) : (
        <p className="mt-3 text-sm text-steel">
          Only staff allowed to lock and unlock doors can use these.
        </p>
      )}
      {sent && (
        <p
          data-testid="door-sent"
          className="mt-3 rounded-md bg-primary/10 px-3 py-2 text-sm text-ink"
        >
          {`${DOOR_WORD[sent]} sent to ${plateNumber ?? 'the car'}. Cartrack does not confirm whether the doors ${DOOR_DONE[sent]}.`}
        </p>
      )}
      {door.commands.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-steel">
            Last door commands
          </h4>
          <ul data-testid="door-history" className="space-y-1 text-sm">
            {door.commands.map((c) => (
              <li key={c.id}>
                <span className="font-medium text-ink">
                  {DOOR_WORD[c.command]} {c.status === 'sent' ? 'sent' : 'refused'}
                </span>
                <span className="text-steel">
                  {' '}
                  by {c.requested_by_name || 'someone'} · {when(c.requested_at)}
                  {c.status === 'refused'
                    ? ` · ${DOOR_REFUSED[c.cartrack_code ?? ''] ?? 'Cartrack could not be reached'}${
                        c.cartrack_code === 'busy' && c.retry_after_s
                          ? `, Cartrack asked to wait ${c.retry_after_s} s`
                          : ''
                      }`
                    : ''}
                  {c.reason ? ` · “${c.reason}”` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {asking && (
        <DoorDialog
          carId={carId}
          plateNumber={plateNumber}
          command={asking}
          onClose={() => setAsking(null)}
          onSent={() => {
            setSent(asking);
            setAsking(null);
          }}
        />
      )}
    </div>
  );
}

function DoorDialog({
  carId,
  plateNumber,
  command,
  onClose,
  onSent,
}: {
  carId: string;
  plateNumber?: string;
  command: DoorCommandWord;
  onClose: () => void;
  onSent: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const lock = command === 'lock';
  const send = useMutation({
    mutationFn: () => cartrackApi.commandDoors(carId, { command, reason: reason.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CARTRACK_KEYS.immobiliser(carId) });
      onSent();
    },
  });
  const failed =
    send.error instanceof Error ? send.error.message : send.error ? 'The command failed.' : null;
  return (
    <Modal
      open
      onClose={onClose}
      title={`${DOOR_WORD[command]} the doors of ${plateNumber ?? 'this car'}?`}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={reason.trim() === ''}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            {DOOR_WORD[command]} doors
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800">
          <p className="font-semibold">
            {lock
              ? 'The doors lock, and the car cannot be started until it is unlocked.'
              : 'The doors open and the car can be started by anyone inside.'}
          </p>
          <p>
            {lock
              ? 'It does not stop a car that is being driven. '
              : 'Only unlock when the customer (or staff) is at the car. '}
            Cartrack does not confirm whether the doors {DOOR_DONE[command]}.
          </p>
        </div>
        <div>
          <label htmlFor="door-reason" className="mb-1 block font-medium text-ink">
            Reason
          </label>
          <textarea
            id="door-reason"
            rows={2}
            maxLength={300}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-md border border-hairline-strong bg-canvas px-3 py-2 text-ink"
          />
          <p className="mt-1 text-xs text-steel">
            Required. Kept on the car's record and the booking's history. The customer is not
            notified.
          </p>
        </div>
        {failed && (
          <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-red-700">
            {nothingSent(failed)}
          </p>
        )}
      </div>
    </Modal>
  );
}
